import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  RESET as SELECTOR_RESET,
  ResetOptions,
  loadOptions,
} from '../../../../../../common/context/actions/selectorActions'
import {
  clearPersistedCatalogFilters,
  clearPersistedCatalogMarcaFilter,
} from '../../../../../../shared/catalogo/utils/catalogoPersistedFilters'
import { NEUMATICOS_CATALOG_KEY } from '../../../../catalogo/store/neumaticoCatalogoTypes'
import { isValidSelectorMarcaId } from '../../../../catalogo/utils/marcaCatalogFilterUtils'
import {
  getTireFromLicense,
  loadDefaultOptions,
  loadOpciones,
} from '../../../../../../common/api/selector'
import {
  selectRunflat,
  selectTipoVehiculo,
  selectAllMedidas,
} from '../../../../../../common/context/selectors/selectorSelectors'

import {
  IMedida,
  MedidaName,
  OrderedMedidas,
  SelectorState,
  TEMPORADA,
} from '../../../../../../common/context/reducers/selectorReducer'
import { HOME_SELECTOR_PLACEHOLDER_MEDIDAS } from '../../../../../../common/components/FunnelSelector/hooks/useSyncedMedidasSelectorFields'

import {
  selectMatriculaMedidas,
  selectMedidas,
  selectMedidasData,
  selectSelectedMedidas,
} from './medidasSelectors'

import {
  clearMatriculaMedidas,
  abortCatalogMedidasEdit,
  ensureMatriculaMedidasDefaults,
  syncLegacyFunnelMedidasToCatalog,
  syncCatalogMedidasToLegacy,
  fetchMedidaOpciones,
  refreshMedidasOpciones,
  restorePreviousMedidas,
  setMedidaLoading,
  setPendingAutoFocusNextMedida,
  setMatriculaMedidasError,
  setMatriculaMedidasLoading,
  setMatriculaMedidasSuccess,
  setMedidasConfirmed,
  setMedidasIniciales,
  setMedidasAlteredOnCatalog,
  setEstacionSeleccionada,
  syncMedidasAlteredOnCatalogWithVehicle,
  setOpcionesMedidas,
  setPreviousSelectedMedidasSnapshot,
  setSelectedMatriculaMeasureId,
  setMarcaFilterOptOut,
  updateMedidaSeleccionada,
  type SearchNeumaticosMedidasStateType,
} from './medidasSlice'

import {
  SearchNeumaticosStateType,
  selectSearchNeumaticos,
} from '../../../searchNeumaticos.store'
import {
  buscarVehiculos,
  selectSelectedVehicle,
  setSelectedVehicle,
} from '../../../IdentificadorVehiculo/Infrastructure/store/vehiculoSlice'
import { apiMedidas } from '../api/apiMedidas'
import { isDevNeumaticosMockEnabled } from '../../../IdentificadorVehiculo/Infrastructure/dev/neumaticosDevConfig'
import { getDevMedidasForVehicle } from '../../../IdentificadorVehiculo/Infrastructure/dev/medidasMockFixtures'
import { getDevTallerNeumatico } from '../../../IdentificadorVehiculo/Infrastructure/dev/tallerMockFixtures'
import { isIdentificacionVehiculoEnabled } from '../../../IdentificadorVehiculo/identificacionVehiculoFeature'
import {
  MatriculaMedidasItem,
  MedidasPorMatriculaResponse,
} from '../../../../../../common/types/MedidasPorMatricula'
import { NeumaticoMatricula } from '../../../../../../common/types/Neumatico'
import { Vehicle } from '../../../../../../common/types/Vehiculo'
import { normalizeMedidaOptions } from '../../../../../../common/api/medidaOptions.utils'
import {
  deriveMedidaOptionsFromMatriculaTyres,
  deriveMedidasFromTyres,
  mergeMedidaOptionLists,
  extractMatriculaMeasureOptions,
  findMatriculaMeasureIdForSelection,
  filterMatriculaMedidasForWorkshopTyre,
  buildPartialAmiInitialFromDerived,
  buildSelectedMedidasFromLegacy,
  catalogHasAnyMedidaSelection,
  catalogMatchesLegacyMedidas,
  completeSelectionOverridesIncompleteAmiTyre,
  hasCompleteMedidasSelection,
  matriculaTyresHaveIncompleteHomologation,
  shouldApplyPartialAmiInitialFromVehicle,
  isMedidaUnlocked,
  neumaticoMatriculaHasValidMedidas,
  neumaticoMatriculaToSelectedMedidas,
  neumaticoMatriculaToTyreMeasure,
  resolveUserMedidasForMatriculaApply,
  selectedMedidasArePartial,
  selectedMedidasMatchSomeMatriculaTyre,
  selectedMedidasMatchSomeMatriculaTyreForVehicleWarning,
  shouldAutoApplyMatriculaDefaults,
  shouldPreserveCatalogMedidasOnVehicleReload,
  tryCompleteMedidasFromMatricula,
} from './medidasPorMatricula.utils'

type MedidasSeleccionadas = { [k in MedidaName]?: string | number | null }

function medidasSelectionKey(selected: MedidasSeleccionadas): string {
  return OrderedMedidas.map((name) => String(selected[name] ?? '')).join('|')
}

function enrichOpcionesWithSelectedMedidas(
  opciones: Partial<SelectorState>,
  selectedMedidas: MedidasSeleccionadas,
  matriculaMedidas: MatriculaMedidasItem[]
): Partial<SelectorState> {
  const result: Partial<SelectorState> = { ...opciones }

  for (const name of OrderedMedidas) {
    const rawSelected = selectedMedidas[name]
    const selected =
      isMedidaUnlocked(name, selectedMedidas) &&
      rawSelected != null &&
      rawSelected !== ''
        ? rawSelected
        : null
    const field = result[name]

    let options =
      field && typeof field === 'object' ? (field.options ?? []) : []

    if (matriculaMedidas.length > 0) {
      const amiOptions = deriveMedidaOptionsFromMatriculaTyres(
        name,
        selectedMedidas,
        matriculaMedidas
      )
      options = mergeMedidaOptionLists(options, amiOptions)
    }

    if (
      selected != null &&
      selected !== '' &&
      !options.some((o) => String(o.value) === String(selected))
    ) {
      options = [{ label: String(selected), value: selected }, ...options]
    }

    result[name] = {
      ...(field && typeof field === 'object' ? field : { loading: false }),
      // Només el valor seleccionat a Redux; no aplicar defaults de l'API
      // (evita omplir carga/velocidad quan llanta encara és "Seleccionar").
      value: selected,
      options,
      loading: false,
    }
  }

  return result
}

function* fetchWorkshopTyre(
  matricula: string,
  vehicle: Vehicle
): Generator<unknown, NeumaticoMatricula | null, NeumaticoMatricula> {
  if (isDevNeumaticosMockEnabled()) {
    const mock = getDevTallerNeumatico(matricula, vehicle.id_vehiculo)
    if (mock) return mock
  }

  try {
    return yield call(getTireFromLicense, matricula)
  } catch {
    return null
  }
}

function buildPriorMedidasForField(
  medidaName: MedidaName,
  selectedMedidas: { [k in MedidaName]?: string | number | null },
  medidasData?: ReturnType<typeof selectMedidasData>
): IMedida[] | null {
  const index = OrderedMedidas.indexOf(medidaName)
  if (index < 0) return null

  const priorMedidas: IMedida[] = []
  for (let i = 0; i < index; i++) {
    const name = OrderedMedidas[i]
    const value =
      selectedMedidas[name] ?? medidasData?.[name]?.value ?? null
    if (value == null || value === '') return null
    priorMedidas.push({ name, value })
  }

  return priorMedidas
}

function buildMedidasForOpcionesApi(
  selectedMedidas: MedidasSeleccionadas
): IMedida[] {
  const filled: IMedida[] = []

  for (const name of OrderedMedidas) {
    const value = selectedMedidas[name]
    if (value == null || value === '') break
    filled.push({ name, value })
  }

  if (filled.length === OrderedMedidas.length) {
    return filled.slice(0, -1)
  }

  return filled
}

function* fetchMedidaOpcionesSaga(
  action: ReturnType<typeof fetchMedidaOpciones>
) {
  const medidaName = action.payload
  const state = yield select()
  const selectedMedidas = selectSelectedMedidas(state)
  const medidasData = selectMedidasData(state)
  const index = OrderedMedidas.indexOf(medidaName)
  const requestSelectionKey = medidasSelectionKey(selectedMedidas)

  if (index < 0) return

  const priorMedidas = buildPriorMedidasForField(
    medidaName,
    selectedMedidas,
    medidasData
  )
  if (index > 0 && !priorMedidas) return

  const currentField = medidasData[medidaName]

  const currentValue =
    currentField?.value ?? selectedMedidas[medidaName] ?? null

  yield put(
    setOpcionesMedidas({
      [medidaName]: {
        loading: true,
        value: currentValue,
        options: currentField?.options ?? [],
      },
    })
  )

  try {
    const tipoVehiculo = selectTipoVehiculo(state)
    const runflat = selectRunflat(state)
    let targetOptions: { label: string; value: string | number | null }[] = []

    if (index === 0) {
      const defaults: Partial<SelectorState> = yield call(
        loadDefaultOptions,
        tipoVehiculo
      )
      targetOptions = defaults[medidaName]?.options ?? []
    } else {
      const opciones: Partial<SelectorState> = yield call(
        loadOpciones as never,
        priorMedidas,
        runflat,
        tipoVehiculo
      )
      targetOptions = opciones?.[medidaName]?.options ?? []
    }

    // Si l'usuari ha cancel·lat (click fora → abort) o ha canviat mides,
    // no aplicar una resposta stale que deixaria el camp a "Seleccionar".
    const latestSelected = selectSelectedMedidas(yield select())
    if (medidasSelectionKey(latestSelected) !== requestSelectionKey) {
      yield put(setMedidaLoading({ name: medidaName, loading: false }))
      return
    }

    targetOptions = normalizeMedidaOptions(targetOptions)

    const matriculaMedidas = selectMatriculaMedidas(yield select())
    if (matriculaMedidas.length > 0) {
      const amiOptions = deriveMedidaOptionsFromMatriculaTyres(
        medidaName,
        latestSelected,
        matriculaMedidas
      )
      targetOptions = mergeMedidaOptionLists(targetOptions, amiOptions)
    }

    const latestValue = latestSelected[medidaName] ?? null
    const valueInOptions =
      latestValue == null ||
      latestValue === '' ||
      targetOptions.some((o) => String(o.value) === String(latestValue))

    yield put(
      setOpcionesMedidas({
        [medidaName]: {
          loading: false,
          value: valueInOptions ? latestValue : null,
          options: targetOptions,
        },
      })
    )
  } catch (err) {
    const latestSelected = selectSelectedMedidas(yield select())
    if (medidasSelectionKey(latestSelected) !== requestSelectionKey) {
      return
    }
    yield put(setMedidaLoading({ name: medidaName, loading: false }))
    console.error('❌ Error cargando opciones al abrir medida:', err)
  }
}

function* loadOpcionesFromSelectedMedidasSaga(options?: {
  requestAutoFocusNext?: boolean
}) {
  const state = yield select()
  const tipoVehiculo = selectTipoVehiculo(state)
  const selectedMedidas = selectSelectedMedidas(state)
  const requestSelectionKey = medidasSelectionKey(selectedMedidas)

  const medidas = buildMedidasForOpcionesApi(selectedMedidas)

  if (!medidas.length) return

  const opciones: Partial<SelectorState> = yield call(
    loadOpciones as never,
    medidas,
    selectRunflat(state),
    tipoVehiculo
  )

  const latestSelected = selectSelectedMedidas(yield select())
  if (medidasSelectionKey(latestSelected) !== requestSelectionKey) {
    return
  }

  const matriculaMedidas = selectMatriculaMedidas(yield select())
  const enriched = opciones
    ? enrichOpcionesWithSelectedMedidas(
        opciones,
        latestSelected,
        matriculaMedidas
      )
    : hasCompleteMedidasSelection(latestSelected) && matriculaMedidas.length
      ? enrichOpcionesWithSelectedMedidas({}, latestSelected, matriculaMedidas)
      : null

  if (enriched) {
    yield put(setOpcionesMedidas(enriched))
    if (
      options?.requestAutoFocusNext &&
      selectedMedidasArePartial(latestSelected)
    ) {
      yield put(setPendingAutoFocusNextMedida(true))
    }
  }
}

function* buscarMedidasSaga() {
  try {
    const selectedMedidas = yield select(selectSelectedMedidas)
    yield call(loadOpcionesFromSelectedMedidasSaga, {
      requestAutoFocusNext: selectedMedidasArePartial(selectedMedidas),
    })
  } catch (err) {
    console.error('❌ Error cargando opciones:', err)
  }
}

function* refreshOpcionesAfterMedidasIniciales(
  action: ReturnType<typeof setMedidasIniciales>
) {
  const payload = action.payload
  try {
    if (selectedMedidasArePartial(payload)) {
      yield call(loadOpcionesFromSelectedMedidasSaga, {
        requestAutoFocusNext: true,
      })
      return
    }
    if (hasCompleteMedidasSelection(payload)) {
      yield call(loadOpcionesFromSelectedMedidasSaga)
    }
  } catch (err) {
    console.error('❌ Error cargando opciones tras setMedidasIniciales:', err)
  }
}

function* refreshOpcionesOnRequest() {
  const selectedMedidas = yield select(selectSelectedMedidas)
  if (!selectedMedidasArePartial(selectedMedidas)) return
  try {
    yield call(loadOpcionesFromSelectedMedidasSaga, {
      requestAutoFocusNext: true,
    })
  } catch (err) {
    console.error('❌ Error cargando opciones (refresh):', err)
  }
}

function* abortCatalogMedidasEditSaga() {
  try {
    yield call(loadOpcionesFromSelectedMedidasSaga)
  } catch (err) {
    console.error('❌ Error restaurando opciones tras cancelar edición:', err)
  }
}

function* applyHomePlaceholderMedidasToCatalogSaga() {
  const medidasState = yield select(selectMedidas)
  if (!medidasState.selectedEstacion) {
    yield put(setEstacionSeleccionada(TEMPORADA.VERANO))
  }

  const opciones: Partial<SearchNeumaticosMedidasStateType['medidasData']> = {}
  const iniciales: MedidasSeleccionadas = {}

  for (const name of OrderedMedidas) {
    const value = HOME_SELECTOR_PLACEHOLDER_MEDIDAS[name]
    opciones[name] = {
      value,
      options: [{ label: String(value), value }],
      loading: false,
    }
    iniciales[name] = value
  }

  yield put(setOpcionesMedidas(opciones))
  yield put(setMedidasIniciales(iniciales))
  yield put(setMedidasConfirmed(true))
  clearPersistedCatalogFilters(NEUMATICOS_CATALOG_KEY)
}

function* syncLegacyFunnelMedidasToCatalogSaga(action?: {
  payload?: { force?: boolean }
}) {
  const force = Boolean(action?.payload?.force)
  const searchState: SearchNeumaticosStateType = yield select(selectSearchNeumaticos)
  const vehicle: Vehicle | undefined = yield select(selectSelectedVehicle)
  const matricula = vehicle?.cod_matricula ?? searchState.vehiculo.matricula
  const medidasState = yield select(selectMedidas)
  const selectedMedidas = medidasState.selectedMedidas

  const appState = yield select()
  const legacyMedidas = selectAllMedidas(appState)
  const legacySelected = buildSelectedMedidasFromLegacy(legacyMedidas)
  const legacyComplete = hasCompleteMedidasSelection(legacySelected)

  if (
    !force &&
    isIdentificacionVehiculoEnabled() &&
    vehicle &&
    (matricula || (vehicle.id_vehiculo_ami && vehicle.id_vivid)) &&
    hasCompleteMedidasSelection(selectedMedidas) &&
    (!legacyComplete ||
      catalogMatchesLegacyMedidas(selectedMedidas, legacySelected))
  ) {
    return
  }

  if (!force && selectedMedidasArePartial(selectedMedidas)) {
    return
  }

  if (
    catalogHasAnyMedidaSelection(selectedMedidas) &&
    legacyComplete &&
    catalogMatchesLegacyMedidas(selectedMedidas, legacySelected) &&
    medidasState.isMedidasConfirmed
  ) {
    return
  }

  // Home → catàleg: no sobreescriure mides confirmades amb legacy incomplet o idèntic.
  // Si el funnel legacy té una selecció completa diferent (p. ex. /selector/mides), sí aplicar-la.
  // `force` (p. ex. area-privada) sempre aplica el legacy complet.
  if (
    !force &&
    hasCompleteMedidasSelection(selectedMedidas) &&
    medidasState.isMedidasConfirmed &&
    (!legacyComplete ||
      catalogMatchesLegacyMedidas(selectedMedidas, legacySelected))
  ) {
    return
  }

  if (
    !force &&
    catalogHasAnyMedidaSelection(selectedMedidas) &&
    selectedMedidasArePartial(selectedMedidas)
  ) {
    return
  }

  if (
    catalogHasAnyMedidaSelection(selectedMedidas) &&
    !legacyComplete
  ) {
    if (
      hasCompleteMedidasSelection(selectedMedidas) &&
      !medidasState.isMedidasConfirmed
    ) {
      yield put(setMedidasConfirmed(true))
    }
    return
  }

  if (!legacyComplete) {
    const canLoadVehicleMedidas =
      vehicle &&
      (matricula || (vehicle.id_vehiculo_ami && vehicle.id_vivid))

    if (
      !catalogHasAnyMedidaSelection(selectedMedidas) &&
      !selectedMedidasArePartial(selectedMedidas) &&
      !canLoadVehicleMedidas
    ) {
      yield call(applyHomePlaceholderMedidasToCatalogSaga)
    }
    return
  }

  const opciones: Partial<SearchNeumaticosMedidasStateType['medidasData']> = {}
  for (const name of OrderedMedidas) {
    const medida = legacyMedidas[name]
    if (!medida) continue
    opciones[name] = {
      ...medida,
      options: medida.options ?? [],
    }
  }

  yield put(setOpcionesMedidas(opciones))
  yield put(setMedidasIniciales(legacySelected))
  yield put(setMedidasConfirmed(true))
  clearPersistedCatalogFilters(NEUMATICOS_CATALOG_KEY)
}


function* syncCatalogMedidasToLegacySaga() {
  const medidasState = yield select(selectMedidas)
  const selectedMedidas = medidasState.selectedMedidas

  if (
    !medidasState.isMedidasConfirmed ||
    !hasCompleteMedidasSelection(selectedMedidas)
  ) {
    return
  }

  const appState = yield select()
  const legacyMedidas = selectAllMedidas(appState)
  const legacySelected = buildSelectedMedidasFromLegacy(legacyMedidas)

  if (catalogMatchesLegacyMedidas(selectedMedidas, legacySelected)) {
    return
  }

  const opts: Partial<SelectorState> = { pristine: false }
  for (const name of OrderedMedidas) {
    const value = selectedMedidas[name] ?? null
    const data = medidasState.medidasData[name]
    const legacy = legacyMedidas[name]
    const options =
      data?.options && data.options.length
        ? data.options
        : value != null && value !== ''
          ? [{ label: String(value), value }]
          : legacy?.options ?? []

    opts[name] = {
      ...(legacy || {
        loading: false,
        value: null,
        default: null,
        options: [],
      }),
      value,
      options,
      loading: false,
    }
  }

  yield put(loadOptions(opts))
}

function* applyMatriculaMedidasFromLoadedData(
  medidasItems: MatriculaMedidasItem[],
  workshopNeumatico: NeumaticoMatricula | null,
  options: { preferVehiclePartialOverLegacy?: boolean } = {}
) {
  const medidasState = yield select(selectMedidas)

  if (shouldPreserveCatalogMedidasOnVehicleReload(medidasState)) {
    if (selectedMedidasArePartial(medidasState.selectedMedidas)) {
      yield call(loadOpcionesFromSelectedMedidasSaga, {
        requestAutoFocusNext: false,
      })
    } else {
      yield put(syncMedidasAlteredOnCatalogWithVehicle())
    }
    return
  }

  let items = medidasItems
  let useKnownWorkshopMeasure = false

  if (neumaticoMatriculaHasValidMedidas(workshopNeumatico)) {
    const workshopTyre = neumaticoMatriculaToTyreMeasure(workshopNeumatico!)
    items = filterMatriculaMedidasForWorkshopTyre(items, workshopTyre)
    useKnownWorkshopMeasure = true
  }

  const measureOptions = extractMatriculaMeasureOptions(items)
  const tyres = measureOptions.map((item) => item.tyre)
  if (!tyres.length) return

  const derived = deriveMedidasFromTyres(tyres)
  if (!derived) return

  const partialAmiInitial = buildPartialAmiInitialFromDerived(derived)
  const shouldPreferPartialAmi =
    Boolean(partialAmiInitial) &&
    matriculaTyresHaveIncompleteHomologation(tyres)

  const appState = yield select()
  const legacySelected = buildSelectedMedidasFromLegacy(
    selectAllMedidas(appState)
  )
  const resolved = resolveUserMedidasForMatriculaApply(
    medidasState.selectedMedidas,
    medidasState.isMedidasConfirmed,
    legacySelected
  )

  if (resolved.shouldSyncLegacyToCatalog && !shouldPreferPartialAmi) {
    const legacyMedidas = selectAllMedidas(appState)
    const opciones: Partial<SearchNeumaticosMedidasStateType['medidasData']> =
      {}
    for (const name of OrderedMedidas) {
      const medida = legacyMedidas[name]
      if (!medida) continue
      opciones[name] = {
        ...medida,
        options: medida.options ?? [],
      }
    }
    yield put(setOpcionesMedidas(opciones))
    yield put(setMedidasIniciales(resolved.selected))
    yield put(setMedidasConfirmed(true))
  }

  const catalogSelected: MedidasSeleccionadas = yield select(
    selectSelectedMedidas
  )
  const userSelected = catalogSelected
  const hasUserCompleteSelection =
    resolved.treatAsUserChoice && hasCompleteMedidasSelection(userSelected)
  const userHasPartialCatalogSelection =
    selectedMedidasArePartial(catalogSelected)

  if (
    shouldPreferPartialAmi &&
    shouldApplyPartialAmiInitialFromVehicle(
      catalogSelected,
      tyres,
      medidasState.vehicleTyreSelectionWasPartial,
      {
        isMedidasConfirmed: medidasState.isMedidasConfirmed,
        medidasAlteredOnCatalog: medidasState.medidasAlteredOnCatalog,
        legacySelected,
        preferVehiclePartialOverLegacy: options.preferVehiclePartialOverLegacy,
      }
    )
  ) {
    yield put(setMedidasIniciales(partialAmiInitial!))
    yield put(
      setSelectedMatriculaMeasureId(
        findMatriculaMeasureIdForSelection(measureOptions, partialAmiInitial!)
      )
    )
    yield put(setMedidasConfirmed(false))
    yield put(setMedidasAlteredOnCatalog(false))
    yield call(loadOpcionesFromSelectedMedidasSaga, {
      requestAutoFocusNext: true,
    })
    return
  }

  const preserveUserTyreChoice =
    hasUserCompleteSelection &&
    selectedMedidasMatchSomeMatriculaTyreForVehicleWarning(userSelected, tyres) &&
    !completeSelectionOverridesIncompleteAmiTyre(
      userSelected,
      tyres,
      medidasState.vehicleTyreSelectionWasPartial
    )

  const preserveIncompatibleUserChoice =
    hasUserCompleteSelection && !preserveUserTyreChoice

  const opcionesParaPut = preserveUserTyreChoice
    ? (Object.fromEntries(
        Object.entries(derived.options).map(([name, data]) => {
          if (!data || typeof data !== 'object') return [name, data]
          const { loading, options } = data as {
            loading: boolean
            options: unknown
          }
          return [name, { loading, options }]
        })
      ) as typeof derived.options)
    : derived.options

  yield put(setOpcionesMedidas(opcionesParaPut))

  if (preserveUserTyreChoice) {
    yield put(syncMedidasAlteredOnCatalogWithVehicle())
    return
  }

  if (preserveIncompatibleUserChoice) {
    yield put(setOpcionesMedidas(derived.options))
    yield put(syncMedidasAlteredOnCatalogWithVehicle())
    return
  }

  const confirmDefaults = function* confirmDefaults() {
    if (!medidasState.deferMedidasCatalogConfirmUntilApply) {
      yield put(setMedidasConfirmed(true))
    }
  }

  if (
    useKnownWorkshopMeasure &&
    workshopNeumatico &&
    !userHasPartialCatalogSelection &&
    !hasUserCompleteSelection
  ) {
    const medidasFromWorkshop =
      neumaticoMatriculaToSelectedMedidas(workshopNeumatico)
    yield put(setMedidasIniciales(medidasFromWorkshop))
    yield put(
      setSelectedMatriculaMeasureId(
        findMatriculaMeasureIdForSelection(measureOptions, medidasFromWorkshop)
      )
    )
    yield put(setPreviousSelectedMedidasSnapshot())
    yield put(setMedidasAlteredOnCatalog(false))
    yield call(confirmDefaults)
    return
  }

  if (shouldAutoApplyMatriculaDefaults(derived, tyres)) {
    yield put(setMedidasIniciales(derived.initial))
    yield put(
      setSelectedMatriculaMeasureId(
        measureOptions.length === 1
          ? measureOptions[0].id
          : findMatriculaMeasureIdForSelection(measureOptions, derived.initial)
      )
    )
    yield put(setPreviousSelectedMedidasSnapshot())
    yield put(setMedidasAlteredOnCatalog(false))
    yield call(confirmDefaults)
    return
  }

  yield put(setSelectedMatriculaMeasureId(null))

  const completed = tryCompleteMedidasFromMatricula(
    medidasState.selectedMedidas,
    items,
    derived.initial
  )
  if (completed) {
    yield put(setMedidasIniciales(completed))
    yield put(
      setSelectedMatriculaMeasureId(
        findMatriculaMeasureIdForSelection(measureOptions, completed)
      )
    )
    yield put(setPreviousSelectedMedidasSnapshot())
    yield put(setMedidasAlteredOnCatalog(false))
    yield call(confirmDefaults)
    return
  }

  if (!hasCompleteMedidasSelection(medidasState.selectedMedidas)) {
    if (
      userHasPartialCatalogSelection ||
      medidasState.vehicleTyreSelectionWasPartial
    ) {
      yield call(loadOpcionesFromSelectedMedidasSaga, {
        requestAutoFocusNext: true,
      })
      return
    }

    yield put(
      setMedidasIniciales({
        ancho: null,
        serie: null,
        llanta: null,
        carga: null,
        velocidad: null,
      })
    )
    yield put(setPendingAutoFocusNextMedida(true))
  }
}

function* ensureMatriculaMedidasDefaultsSaga() {
  let medidasState = yield select(selectMedidas)

  // Tornada des de home amb mides preservades: completes però isMedidasConfirmed=false → no es cercava.
  if (
    hasCompleteMedidasSelection(medidasState.selectedMedidas) &&
    !medidasState.isMedidasConfirmed &&
    !medidasState.medidasAlteredOnCatalog
  ) {
    yield put(setMedidasConfirmed(true))
    return
  }

  if (!isIdentificacionVehiculoEnabled()) {
    yield call(syncLegacyFunnelMedidasToCatalogSaga)
    return
  }

  const searchState: SearchNeumaticosStateType = yield select(selectSearchNeumaticos)
  const vehicle: Vehicle | undefined = yield select(selectSelectedVehicle)
  const matricula = vehicle?.cod_matricula ?? searchState.vehiculo.matricula
  const canLoadAmi =
    vehicle &&
    (matricula || (vehicle.id_vehiculo_ami && vehicle.id_vivid))

  if (!canLoadAmi) {
    yield call(syncLegacyFunnelMedidasToCatalogSaga)
    return
  }

  if (!hasCompleteMedidasSelection(medidasState.selectedMedidas)) {
    yield call(syncLegacyFunnelMedidasToCatalogSaga)
    medidasState = yield select(selectMedidas)
  }

  const hasCompleteConfirmedMedidas =
    medidasState.isMedidasConfirmed &&
    hasCompleteMedidasSelection(medidasState.selectedMedidas)

  if (hasCompleteConfirmedMedidas) {
    if (!medidasState.matriculaMedidas.length) {
      yield call(loadMatriculaMedidasOnlyForCatalog)
      return
    }

    yield put(syncMedidasAlteredOnCatalogWithVehicle())
    return
  }

  if (!medidasState.matriculaMedidas.length) {
    yield call(handleSelectedVehicleChange)
    return
  }

  if (shouldPreserveCatalogMedidasOnVehicleReload(medidasState)) {
    yield put(syncMedidasAlteredOnCatalogWithVehicle())
    return
  }

  const workshopNeumatico: NeumaticoMatricula | null = yield call(
    fetchWorkshopTyre,
    matricula,
    vehicle
  )
  yield call(
    applyMatriculaMedidasFromLoadedData,
    medidasState.matriculaMedidas,
    workshopNeumatico
  )
}

function* fetchMatriculaMedidasItemsForVehicle() {
  const searchState: SearchNeumaticosStateType = yield select(selectSearchNeumaticos)
  const vehicle: Vehicle | undefined = yield select(selectSelectedVehicle)
  const matricula = vehicle?.cod_matricula ?? searchState.vehiculo.matricula

  if (
    !vehicle ||
    (!matricula && !(vehicle.id_vehiculo_ami && vehicle.id_vivid))
  ) {
    return null
  }

  let response: MedidasPorMatriculaResponse

  if (isDevNeumaticosMockEnabled()) {
    const mockResponse = getDevMedidasForVehicle(vehicle)
    if (mockResponse) {
      response = mockResponse
    } else {
      const payload =
        vehicle.id_vehiculo_ami && vehicle.id_vivid
          ? {
              ami_id: vehicle.id_vehiculo_ami,
              vivid_id: vehicle.id_vivid,
            }
          : { matriculaNumber: matricula }
      response = yield call(apiMedidas.medidas, payload)
    }
  } else {
    const payload =
      vehicle.id_vehiculo_ami && vehicle.id_vivid
        ? {
            ami_id: vehicle.id_vehiculo_ami,
            vivid_id: vehicle.id_vivid,
          }
        : { matriculaNumber: matricula }
    response = yield call(apiMedidas.medidas, payload)
  }

  const workshopNeumatico: NeumaticoMatricula | null = yield call(
    fetchWorkshopTyre,
    matricula,
    vehicle
  )

  let medidasItems = response.medidas

  if (neumaticoMatriculaHasValidMedidas(workshopNeumatico)) {
    const workshopTyre = neumaticoMatriculaToTyreMeasure(workshopNeumatico!)
    medidasItems = filterMatriculaMedidasForWorkshopTyre(
      medidasItems,
      workshopTyre
    )
  }

  return { medidasItems, workshopNeumatico }
}

/** Carrega AMI per toast/opcions sense sobreescriure mides confirmades per l'usuari. */
function* loadMatriculaMedidasOnlyForCatalog() {
  yield put(setMatriculaMedidasLoading())

  try {
    const loaded = yield call(fetchMatriculaMedidasItemsForVehicle)
    if (!loaded) {
      yield put(setMatriculaMedidasError('Vehicle not found'))
      return
    }

    yield put(setMatriculaMedidasSuccess(loaded.medidasItems))
    yield put(syncMedidasAlteredOnCatalogWithVehicle())
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Error loading vehicle tyre sizes'
    yield put(setMatriculaMedidasError(message))
    console.error('❌ Error cargando medidas por matrícula:', err)
  }
}

function* handleSelectedVehicleChange() {
  if (!isIdentificacionVehiculoEnabled()) {
    return
  }

  const medidasState = yield select(selectMedidas)

  if (shouldPreserveCatalogMedidasOnVehicleReload(medidasState)) {
    if (!medidasState.matriculaMedidas.length) {
      yield call(loadMatriculaMedidasOnlyForCatalog)
    } else {
      yield put(syncMedidasAlteredOnCatalogWithVehicle())
    }
    return
  }

  yield put(setMatriculaMedidasLoading())
  yield call(syncLegacyFunnelMedidasToCatalogSaga)

  try {
    const loaded = yield call(fetchMatriculaMedidasItemsForVehicle)
    if (!loaded) {
      yield put(setMatriculaMedidasError('Vehicle not found'))
      return
    }

    yield put(setMatriculaMedidasSuccess(loaded.medidasItems))
    yield call(
      applyMatriculaMedidasFromLoadedData,
      loaded.medidasItems,
      loaded.workshopNeumatico,
      { preferVehiclePartialOverLegacy: true }
    )
    yield put(syncMedidasAlteredOnCatalogWithVehicle())
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Error loading vehicle tyre sizes'
    yield put(setMatriculaMedidasError(message))
    console.error('❌ Error cargando medidas por matrícula:', err)
  }
}

function* clearMatriculaMedidasOnNewPlateSearch(
  action: ReturnType<typeof buscarVehiculos>
) {
  const searchState: SearchNeumaticosStateType = yield select(selectSearchNeumaticos)
  const medidasState = yield select(selectMedidas)

  const isSameMatriculaReopen =
    searchState.vehiculo.selectedVehicle != null &&
    searchState.vehiculo.matricula?.toUpperCase() ===
      action.payload.toUpperCase()

  if (
    isSameMatriculaReopen &&
    (medidasState.matriculaMedidas.length > 0 ||
      shouldPreserveCatalogMedidasOnVehicleReload(medidasState))
  ) {
    return
  }

  yield put(clearMatriculaMedidas())
}

function* onSelectorReset(action: { payload: ResetOptions }) {
  const marca = action.payload.marca
  const hasMarca = isValidSelectorMarcaId(marca as number | null | undefined)
  yield put(setMarcaFilterOptOut(!hasMarca))
  if (hasMarca) {
    clearPersistedCatalogMarcaFilter(NEUMATICOS_CATALOG_KEY)
  }
}

export function* medidasSaga() {
  yield takeLatest(SELECTOR_RESET, onSelectorReset)
  yield takeLatest(updateMedidaSeleccionada.type, buscarMedidasSaga)
  yield takeLatest(fetchMedidaOpciones.type, fetchMedidaOpcionesSaga)
  yield takeLatest(setMedidasIniciales.type, refreshOpcionesAfterMedidasIniciales)
  yield takeLatest(refreshMedidasOpciones.type, refreshOpcionesOnRequest)
  yield takeLatest(buscarVehiculos.type, clearMatriculaMedidasOnNewPlateSearch)
  yield takeLatest(setSelectedVehicle.type, handleSelectedVehicleChange)
  yield takeLatest(abortCatalogMedidasEdit.type, abortCatalogMedidasEditSaga)
  yield takeLatest(restorePreviousMedidas.type, abortCatalogMedidasEditSaga)
  yield takeLatest(
    ensureMatriculaMedidasDefaults.type,
    ensureMatriculaMedidasDefaultsSaga
  )
  yield takeLatest(
    syncLegacyFunnelMedidasToCatalog.type,
    syncLegacyFunnelMedidasToCatalogSaga
  )
  yield takeLatest(
    syncCatalogMedidasToLegacy.type,
    syncCatalogMedidasToLegacySaga
  )
  yield takeLatest(setMedidasConfirmed.type, syncCatalogMedidasToLegacyOnConfirmSaga)
  yield takeLatest(
    updateMedidaSeleccionada.type,
    syncCatalogMedidasToLegacyAfterCatalogEditSaga
  )
}

function* syncCatalogMedidasToLegacyOnConfirmSaga(action: { payload: boolean }) {
  if (!action.payload) return
  yield call(syncCatalogMedidasToLegacySaga)
}

function* syncCatalogMedidasToLegacyAfterCatalogEditSaga() {
  yield call(syncCatalogMedidasToLegacySaga)
}
